import {createFileRoute, useNavigate, useRouter} from "@tanstack/react-router";
import {IconToggleSwitch, ToggleBar} from "../../../components/ToggleBar.tsx";
import {Box, Stack} from "@mui/joy";
import {Facet, facetIcons} from "../../../components/Facet.tsx";
import {editModeIcons, EditorMode, validateEditModeSearchParams} from "../../../forms.tsx";
import {useEffect, useState} from "react";
import {WasteFlavorStub, watchWasteFlavorByIdTqo} from "../../../rpc/webrpc-client.gen.ts";
import {validateAsofSearchParams} from "../../../tstzspan.ts";
import {useWasteAsof} from "../../../waste/waste.tsx";
import {useSelfShiftInfo} from "../../../agent/diary.tsx";
import {WasteCargoView} from "../../../waste/cargo.tsx";
import {buildActionButtons} from "../../../components/ActionButton.tsx";
import {useInspectorWasteActions} from "../../../inspector/InspectorWasteTile.tsx";
import {useBomb} from "../../../bomb/bomb.tsx";
import {useInfallibleQuery} from "../../../util/useInfallibleQuery.tsx";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {useMothership} from "../../../tq/tqx.tsx";

const actionButtonSx = {px: 4, py: 1.8, fontSize: "lg", mx: 3};

function WasteCargoPage() {
    const {id: wasteId} = Route.useParams();
    const {editMode: routeEditMode, asof} = Route.useSearch();
    const navigate = useNavigate();
    const {history: routerHistory} = useRouter();
    const bomb = useBomb();
    const mama = useMothership();
    const {data: shiftInfo} = useSelfShiftInfo();
    const shiftStatus = shiftInfo?.status;
    const [editMode, setEditMode] = useState<EditorMode>(routeEditMode ?? "r");

    const wasteTqrv = useWasteAsof({wasteId, asof});
    const waste = wasteTqrv.data?.waste;
    const isActive = wasteTqrv.data?.isActive;
    const flavorTqrv = useInfallibleQuery(watchWasteFlavorByIdTqo(mama, waste
        ? {wasteFlavorId: waste.flavor.id} : undefined));
    const xflavor = flavorTqrv.data;
    const [marqueeFlavor, setMarqueeFlavor] = useState<WasteFlavorStub | undefined>(xflavor);
    useEffect(() => {
        setMarqueeFlavor(xflavor);
    }, [xflavor]);
    
    const inspectActionsTqrv = useInspectorWasteActions({wasteId});
    const inspectActionProps = inspectActionsTqrv.data
        ? inspectActionsTqrv.data.map(orig => {
            const origPerform = orig.elem.perform;
            let perform: typeof origPerform;
            if (origPerform) {
                perform = () => origPerform().then(() => {
                    routerHistory.back();
                })
            }
            return {...orig, elem: {...orig.elem, perform}};
        })
        : undefined;

    return <PageShell>
        <PageHeader>
            <Stack direction="column">
                <TopBar/>

                <ToggleBar>
                    <Box sx={{flex: 0}}>
                        <IconToggleSwitch dispatchValue={(next: Facet) => {
                            if (next === "timeline") {
                                navigate({to: "/waste/$id/timeline", params: {id: wasteId}})
                                    .catch(bomb);
                            }
                        }} icons={facetIcons} value="cargo"/>
                    </Box>
                    <Stack direction="row" sx={{justifyContent: "center", alignItems: "center", flex: 1}}>
                        {marqueeFlavor
                            ? <Box sx={{fontSize: "lg"}}>{marqueeFlavor.displayName}</Box>
                            : null}
                    </Stack>
                    <IconToggleSwitch icons={editModeIcons} value={editMode} dispatchValue={setEditMode}
                                      disabled={shiftStatus !== "onDuty" || !isActive}/>
                </ToggleBar>
            </Stack>
        </PageHeader>
        <PageBody>
            <WasteCargoView wasteId={wasteId} mode={editMode} asof={asof} active={!asof}
                            onFlavorChange={(newFlavor) => {
                                setMarqueeFlavor(newFlavor);
                            }}
                            onAsofChange={(newAsof) => {
                                void navigate({
                                    to: Route.to,
                                    params: {id: wasteId},
                                    search: {asof: newAsof},
                                    replace: true,
                                });
                            }}
                            afterPatch={() => {
                                setEditMode("r");
                            }}
            />
            {
                editMode === "r"
                    ? <Stack direction="row" sx={{alignItems: "center", justifyContent: "space-evenly"}}>
                        {buildActionButtons(actionButtonSx, inspectActionProps)}
                    </Stack>
                    : null
            }
        </PageBody>
    </PageShell>;
}

function validateSearch(raw: Record<string, unknown>) {
    return {
        ...validateEditModeSearchParams(raw),
        ...validateAsofSearchParams(raw),
    };
}

export const Route = createFileRoute("/_auth/waste/$id/cargo")({
    component: WasteCargoPage,
    validateSearch,
});

